import { DatabricksDashboard } from '@databricks/aibi-client';
import { useEffect, useRef, useState } from 'react';

import { Spinner } from '~/components/Spinner';
import type { DashboardEmbedConfig } from '~/lib/databricks/types';
import { ErrorContent } from '~/modules/errors/components/ErrorContent';

interface DatabricksEmbedDashboardProps {
  config: DashboardEmbedConfig;
  ariaLabel: string;
}

export function DatabricksEmbedDashboard({
  config,
  ariaLabel,
}: DatabricksEmbedDashboardProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dashboardRef = useRef<DatabricksDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    let isStale = false;
    let dashboard: DatabricksDashboard | null = null;

    const initDashboard = async () => {
      try {
        setIsLoading(true);
        setError(null);

        dashboard = new DatabricksDashboard({
          instanceUrl: config.workspaceUrl,
          workspaceId: config.workspaceId,
          dashboardId: config.dashboardId,
          token: config.embedToken,
          container: containerRef.current!,
        });

        dashboardRef.current = dashboard;

        await dashboard.initialize();

        if (isStale) {
          return;
        }

        setIsLoading(false);
      } catch (err) {
        dashboard?.destroy?.();
        dashboardRef.current = null;

        if (isStale) {
          return;
        }

        console.error('Error initializing Databricks dashboard:', err);
        setError(
          err instanceof Error ? err : new Error('Failed to load dashboard'),
        );
        setIsLoading(false);
      }
    };

    initDashboard();

    return () => {
      isStale = true;
      dashboardRef.current?.destroy?.();
      dashboardRef.current = null;
    };
  }, [
    config.workspaceUrl,
    config.workspaceId,
    config.dashboardId,
    config.embedToken,
  ]);

  if (error) {
    return <ErrorContent type="default" variant="inline" />;
  }

  return (
    <div className="relative h-full w-full">
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : null}
      <div
        ref={containerRef}
        role="region"
        aria-label={ariaLabel}
        className="h-full w-full"
      />
    </div>
  );
}

DatabricksEmbedDashboard.displayName = 'DatabricksEmbedDashboard';
